// Where your pay is sent — the employee's payout methods (employee-service is
// the system of record; only masked forms ever reach the client). Editing stays
// in the profile's payment-methods surface; this card shows status + links.
// NOTE: per-payslip disbursement status is a payroll-service Phase-5 follow-up
// (design doc §1 gap 1) — this shows the configured destination, not a receipt.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Landmark, Smartphone, ExternalLink } from 'lucide-react';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import type { PaymentMethod } from '@/lib/hrApi';
import { sortForDisplay } from '@/features/payment-methods/components/PaymentMethodChips';

interface PaymentDestinationCardProps {
  methods?: PaymentMethod[];
  loading: boolean;
  error: boolean;
  onManage: () => void;
}

const STATUS_META: Record<string, { labelKey: string; tone: 'success' | 'warning' | 'danger' | 'secondary' }> = {
  APPROVED: { labelKey: 'myPay.pm.verified', tone: 'success' },
  PENDING_VERIFICATION: { labelKey: 'myPay.pm.pending', tone: 'warning' },
  REJECTED: { labelKey: 'myPay.pm.rejected', tone: 'danger' },
  INACTIVE: { labelKey: 'myPay.pm.inactive', tone: 'secondary' },
};

export const PaymentDestinationCard: React.FC<PaymentDestinationCardProps> = ({ methods, loading, error, onManage }) => {
  const { t } = useTranslation('payroll');

  const active = (methods ?? []).filter((m) => m.status !== 'INACTIVE');
  // The salary default first, then verified, then the rest — show at most two.
  const shown = sortForDisplay(active).slice(0, 2);

  return (
    <section aria-labelledby="mypay-destination" className="rounded-xl border border-border p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 id="mypay-destination" className="text-sm font-semibold text-charcoal">{t('myPay.destinationTitle')}</h2>
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={onManage}>
          {t('myPay.managePaymentMethods')} <ExternalLink className="h-3.5 w-3.5" />
        </Button>
      </div>

      {loading ? (
        <Skeleton className="mt-3 h-16 w-full" />
      ) : error ? (
        <p className="mt-3 text-sm text-muted-foreground">{t('myPay.sectionUnavailable')}</p>
      ) : shown.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">{t('myPay.noPaymentMethods')}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {shown.map((m) => {
            const bank = m.type === 'BANK';
            const meta = STATUS_META[m.status] ?? STATUS_META.INACTIVE;
            return (
              <li key={m.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex min-w-0 items-center gap-2 text-charcoal">
                  {bank ? <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" /> : <Smartphone className="h-4 w-4 shrink-0 text-muted-foreground" />}
                  <span className="truncate">
                    {bank ? m.bank_name ?? t('myPay.pm.bank') : m.mobile_money_provider ?? t('myPay.pm.mobileMoney')}
                    <span className="ml-1.5 tabular-nums text-muted-foreground">
                      {bank ? m.account_masked : m.phone_masked}
                    </span>
                  </span>
                  {m.is_default && (
                    <Badge variant="default" className="text-[10px]">
                      {t('destinations.default', { defaultValue: 'Default for salary' })}
                    </Badge>
                  )}
                </span>
                <Badge variant={meta.tone as any} className="shrink-0 text-[10px]">{t(meta.labelKey)}</Badge>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
